import { Component, EventEmitter, Input, OnChanges, Output, TemplateRef } from "@angular/core";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { IPlanWeekItem } from "../../../domain/models/plan/plan-week-item.model";
import { DEFAULT_PLANNER_CONFIG, IPlannerConfig } from "../planner.model";

export interface IPlannerDay {
  date: Date;
  items: IPlanWeekItem[];
}

/** Where an item was dropped; the host turns it into an assign or an unassign call. */
export interface IPlannerAssignEvent {
  item: IPlanWeekItem;
  date: Date | null;
}

const DAYS_IN_WEEK = 7;

/**
 * The weekly plan laid out by day — the "Günler" view. Items that have no day yet sit in the strip
 * above and are dragged onto a day; dragging one back off clears its day again.
 *
 * The board only reports the move; saving it is the host's business.
 */
@Component({
  selector: "app-planner-day-board",
  templateUrl: "./planner-day-board.component.html",
  styleUrl: "./planner-day-board.component.scss",
})
export class PlannerDayBoardComponent implements OnChanges {
  /** First day of the week the plan is showing. */
  @Input() weekStart?: Date | string | null;
  @Input() items: IPlanWeekItem[] = [];
  @Input() config: IPlannerConfig = DEFAULT_PLANNER_CONFIG;
  @Input() videoTemplate?: TemplateRef<{ $implicit: IPlanWeekItem }> | null;
  @Input() menuTemplate?: TemplateRef<{ $implicit: IPlanWeekItem }> | null;

  @Output() itemAssigned = new EventEmitter<IPlannerAssignEvent>();
  @Output() itemToggled = new EventEmitter<IPlanWeekItem>();
  @Output() itemTransferRequested = new EventEmitter<IPlanWeekItem>();
  @Output() itemDuplicateRequested = new EventEmitter<IPlanWeekItem>();
  @Output() itemRemoveRequested = new EventEmitter<IPlanWeekItem>();
  @Output() itemMenuOpened = new EventEmitter<IPlanWeekItem>();

  days: IPlannerDay[] = [];
  unassignedItems: IPlanWeekItem[] = [];
  /** The lessons the unassigned items belong to; the strip is browsed one lesson at a time. */
  lessonFilters: { id: number | null; name: string }[] = [];
  selectedLessonId: number | null = null;

  ngOnChanges(): void {
    this.buildDays();
    this.buildLessonFilters();
  }

  visibleUnassignedItems(): IPlanWeekItem[] {
    return this.unassignedItems.filter((item) => (item.lessonId ?? null) === this.selectedLessonId);
  }

  selectLesson(lessonId: number | null) {
    this.selectedLessonId = lessonId;
  }

  /**
   * One chip per lesson waiting to be scheduled, in the order the items came in. Items with no
   * lesson at all — free notes — get their own chip instead of disappearing from the strip.
   */
  private buildLessonFilters() {
    const filters: { id: number | null; name: string }[] = [];

    for (const item of this.unassignedItems) {
      const id = item.lessonId ?? null;
      if (filters.some((filter) => filter.id === id)) {
        continue;
      }

      filters.push({ id, name: item.lesson?.name ?? "" });
    }

    this.lessonFilters = filters;

    const isVisible = filters.some((filter) => filter.id === this.selectedLessonId);
    if (!isVisible) {
      this.selectedLessonId = filters[0]?.id ?? null;
    }
  }

  /**
   * Days keep the time of day the week starts at instead of being pulled to local midnight: a week
   * that starts at midnight UTC would otherwise slide a day back east of Greenwich, and the day an
   * item is dropped on would be saved as the one before it.
   */
  private buildDays() {
    const start = this.weekStart ? new Date(this.weekStart) : new Date();

    this.days = Array.from({ length: DAYS_IN_WEEK }, (_, index) => {
      const date = new Date(start);
      date.setUTCDate(date.getUTCDate() + index);

      return { date, items: this.items.filter((item) => this.isSameDay(item.assignedDate, date)) };
    });

    this.unassignedItems = this.items.filter((item) => !item.assignedDate);
  }

  /** Both sides are compared on the calendar day the API speaks in. */
  private isSameDay(assignedDate: Date | string | null, date: Date): boolean {
    if (!assignedDate) {
      return false;
    }

    return this.dayKey(new Date(assignedDate)) === this.dayKey(date);
  }

  private dayKey(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  onDroppedToDay(event: CdkDragDrop<IPlanWeekItem[]>, day: IPlannerDay) {
    const item = event.item.data as IPlanWeekItem;
    if (!item || event.previousContainer === event.container) {
      return;
    }

    this.itemAssigned.emit({ item, date: day.date });
  }

  onDroppedToUnassigned(event: CdkDragDrop<IPlanWeekItem[]>) {
    const item = event.item.data as IPlanWeekItem;
    if (!item || event.previousContainer === event.container) {
      return;
    }

    this.itemAssigned.emit({ item, date: null });
  }
}
